<script setup lang="ts">
  import { IonSkeletonText } from '@ionic/vue'
  import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
  import { useI18n } from 'vue-i18n'
  import PrimaryActionButton from '@/components/buttons/PrimaryActionButton.vue'

  const { t } = useI18n()

  const props = withDefaults(
    defineProps<{
      src: string
      loop?: boolean
      disableBorderRadius?: boolean
    }>(),
    {
      autoplay: true,
      loop: false,
      disableBorderRadius: false,
    },
  )

  const emit = defineEmits<{
    play: []
    pause: []
    loading: [loading: boolean]
    progress: [progress: number]
    completed: []
    error: [error: Event]
  }>()

  const videoRef = ref<HTMLVideoElement>()
  const showIcon = ref(false)
  const isPlaying = ref(false)
  const isLoading = ref(true)
  const didStartedOncePlaying = ref(false)
  const hasError = ref(false)
  const userPaused = ref(false)
  const progress = ref(0)
  const progressUpdateId = ref<number | null>(null)

  const iconType = computed(() => {
    return isPlaying.value ? 'play' : 'pause'
  })

  // Expose pause method to parent component
  const pause = () => {
    console.debug('[VideoCard] pause() called', { src: props.src })
    if (videoRef.value && !videoRef.value.paused) {
      videoRef.value.pause()
      isPlaying.value = false
      userPaused.value = true
      stopProgressUpdates()
      emit('pause')
    }
  }

  // Expose play method to parent component
  const play = () => {
    console.debug('[VideoCard] play() called', { src: props.src })
    if (videoRef.value && videoRef.value.paused) {
      void videoRef.value.play()
      isPlaying.value = true
      userPaused.value = false
      startProgressUpdates()
      emit('play')
    }
  }

  const onVideoPaused = () => {
    console.debug('[VideoCard] paused', {
      currentTime: videoRef.value?.currentTime,
    })
    isPlaying.value = false
    emit('pause')
  }

  // Update progress more frequently using requestAnimationFrame
  const updateProgress = () => {
    if (!videoRef.value || videoRef.value.paused || hasError.value) {
      progressUpdateId.value = null
      return
    }

    const currentTime = videoRef.value.currentTime
    const duration = videoRef.value.duration

    if (duration > 0) {
      const progressPercent = (currentTime / duration) * 100
      progress.value = progressPercent
      emit('progress', progressPercent)
    }

    // Continue updating while playing
    progressUpdateId.value = requestAnimationFrame(updateProgress)
  }

  // Start frequent progress updates
  const startProgressUpdates = () => {
    if (progressUpdateId.value === null) {
      progressUpdateId.value = requestAnimationFrame(updateProgress)
    }
  }

  // Stop frequent progress updates
  const stopProgressUpdates = () => {
    if (progressUpdateId.value !== null) {
      cancelAnimationFrame(progressUpdateId.value)
      progressUpdateId.value = null
    }
  }

  // Reset video to beginning
  const resetToStart = () => {
    if (!videoRef.value) return

    // Only reset if not already at the beginning to prevent unnecessary events
    if (videoRef.value.currentTime > 0.1) {
      videoRef.value.currentTime = 0
      progress.value = 0

      emit('progress', 0)
    }
  }

  defineExpose({
    pause,
    play,
    resetToStart,
  })

  const togglePlayPause = () => {
    console.debug('[VideoCard] togglePlayPause', {
      paused: videoRef.value?.paused,
      hasError: hasError.value,
    })
    if (!videoRef.value || hasError.value) return

    if (videoRef.value.paused) {
      void videoRef.value.play()
      isPlaying.value = true
      userPaused.value = false
      startProgressUpdates()
    } else {
      videoRef.value.pause()
      isPlaying.value = false
      userPaused.value = true
      stopProgressUpdates()
    }

    showIcon.value = true
    setTimeout(() => {
      showIcon.value = false
    }, 800)
  }

  const onVideoCanPlay = () => {
    console.debug('[VideoCard] canplay', {
      src: props.src,
      userPaused: userPaused.value,
    })
    console.debug('[VideoCard] canplay event fired', hasError.value)
    isPlaying.value = true
    if (hasError.value) {
      void videoRef.value?.play()
      console.debug('[VideoCard] autoplaying video')
    }

    userPaused.value = false
    hasError.value = false
    isLoading.value = false
    emit('loading', false)
  }

  const onVideoWaiting = () => {
    console.debug('[VideoCard] waiting', { userPaused: userPaused.value })
    if (!userPaused.value) {
      isLoading.value = true
      emit('loading', true)
    }
  }

  const onVideoPlaying = () => {
    console.debug('[VideoCard] playing', {
      currentTime: videoRef.value?.currentTime,
      duration: videoRef.value?.duration,
    })
    if (!didStartedOncePlaying.value) {
      didStartedOncePlaying.value = true
      isLoading.value = false
      emit('loading', false)
    }
    startProgressUpdates()
  }

  const onVideoError = (event: Event) => {
    console.debug('[VideoCard] error', {
      event,
      mediaError: videoRef.value?.error,
    })
    isLoading.value = false
    hasError.value = true
    emit('loading', false)
    emit('error', event)
  }

  const onVideoLoadStart = () => {
    console.debug('[VideoCard] loadstart', { src: props.src })
    isLoading.value = true
    emit('loading', true)
  }

  const onVideoTimeUpdate = () => {
    if (!videoRef.value) return

    const currentTime = videoRef.value.currentTime
    const duration = videoRef.value.duration

    if (duration > 0) {
      const progressPercent = (currentTime / duration) * 100
      progress.value = progressPercent
      console.debug('[VideoCard] timeupdate', {
        currentTime,
        duration,
        progress: progressPercent,
      })
      emit('progress', progressPercent)
    }
  }

  const onVideoEnded = () => {
    console.debug('[VideoCard] ended', {
      currentTime: videoRef.value?.currentTime,
      duration: videoRef.value?.duration,
      loop: props.loop,
    })
    stopProgressUpdates()
    if (!props.loop && videoRef.value) {
      // Only emit completed if video actually reached near the end
      // This prevents false completions from reset or other edge cases
      const currentTime = videoRef.value.currentTime
      const duration = videoRef.value.duration

      if (duration > 0 && currentTime > duration * 0.95) {
        isPlaying.value = false
        emit('completed')
      }
    }
  }

  const reset = () => {
    if (!videoRef.value) return

    console.debug('[VideoCard] reset()', { src: props.src })
    isLoading.value = true
    didStartedOncePlaying.value = false
    userPaused.value = false
    progress.value = 0
    stopProgressUpdates()

    videoRef.value.currentTime = 0
    videoRef.value.pause()
    videoRef.value.load()
  }

  onMounted(() => {
    console.debug('[VideoCard] mounted', { src: props.src })
    emit('loading', true)
  })

  onBeforeUnmount(() => {
    console.debug('[VideoCard] beforeUnmount')
    stopProgressUpdates()
  })
</script>

<template>
  <div
    class="video-card"
    :class="{ 'no-border-radius': props.disableBorderRadius }"
  >
    <div
      class="video-container"
      :class="{ 'no-border-radius': props.disableBorderRadius }"
    >
      <video
        ref="videoRef"
        :autoplay="false"
        :loop="loop"
        :muted="false"
        :controls="false"
        :class="{
          'no-border-radius': props.disableBorderRadius,
        }"
        poster="data:image/gif;base64,R0lGODdhAQABAIABAAAAAP///ywAAAAAAQABAAACAkwBADs="
        playsinline
        disablepictureinpicture
        disableremoteplayback
        class="video-element"
        @click="togglePlayPause"
        @pause="onVideoPaused"
        @loadstart="onVideoLoadStart"
        @canplay="onVideoCanPlay"
        @waiting="onVideoWaiting"
        @playing="onVideoPlaying"
        @error="onVideoError"
        @timeupdate="onVideoTimeUpdate"
        @ended="onVideoEnded"
      >
        <source :src="src" type="video/mp4" @error="onVideoError" />
        <p>{{ t('phrases.video_not_supported') }}</p>
      </video>

      <!-- Loading Skeleton -->
      <div
        v-if="isLoading && !hasError"
        class="loading-overlay"
        :class="{ 'no-border-radius': props.disableBorderRadius }"
      >
        <ion-skeleton-text
          class="video-skeleton"
          :class="{ 'no-border-radius': props.disableBorderRadius }"
          animated
        ></ion-skeleton-text>
      </div>

      <!-- Error State -->
      <div
        v-if="hasError"
        class="error-overlay"
        :class="{ 'no-border-radius': props.disableBorderRadius }"
      >
        <div class="error-content">
          <div class="error-icon text-clockin-text">
            <font-awesome-icon icon="fas fa-wifi-slash" />
          </div>
          <p class="error-message text-clockin-text">
            {{ t('phrases.video_loading_error') }}
          </p>
          <primary-action-button @click="reset">
            <font-awesome-icon icon="fas fa-rotate" />
            <span class="ml-2">
              {{ t('phrases.video_retry') }}
            </span>
          </primary-action-button>
        </div>
      </div>

      <!-- Play/Pause Icon Overlay -->
      <div v-if="showIcon && !hasError" class="icon-overlay">
        <div class="icon-container">
          <div v-if="iconType === 'play'" class="play-icon">
            <div class="triangle"></div>
          </div>
          <div v-else class="pause-icon">
            <div class="bar"></div>
            <div class="bar"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .video-card {
    background: white;
    border-radius: 16px;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  .video-card.no-border-radius {
    border-radius: 0;
  }

  .video-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    position: relative;
    border-radius: 12px;
    overflow: hidden;
  }

  .video-container.no-border-radius {
    border-radius: 0;
  }

  .video-element {
    width: 100%;
    height: 100%;
    object-fit: cover;
    cursor: pointer;
    border-radius: 8px;
    background-color: #fff;
  }

  .video-element.no-border-radius {
    border-radius: 0;
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 5;
    border-radius: 8px;
    overflow: hidden;
  }

  .loading-overlay.no-border-radius {
    border-radius: 0;
  }

  .video-skeleton {
    width: 100%;
    height: 100%;
    border-radius: 8px;
    margin: 0;
  }

  .video-skeleton.no-border-radius {
    border-radius: 0;
  }

  .error-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.1);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 6;
    border-radius: 8px;
  }

  .error-overlay.no-border-radius {
    border-radius: 0;
  }

  .error-content {
    text-align: center;
  }

  .error-icon {
    font-size: 48px;
    margin-bottom: 12px;
  }

  .error-message {
    margin: 0 0 16px 0;
    color: #666;
    font-weight: 500;
  }

  .icon-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 10;
    animation: fadeInOut 0.8s ease-in-out forwards;
  }

  .icon-container {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 80px;
    height: 80px;
    background: rgba(0, 0, 0, 0.7);
    border-radius: 50%;
  }

  .pause-icon {
    display: flex;
    gap: 6px;
  }

  .pause-icon .bar {
    width: 6px;
    height: 24px;
    background: white;
    border-radius: 1px;
  }

  .play-icon .triangle {
    width: 0;
    height: 0;
    border-left: 20px solid white;
    border-top: 12px solid transparent;
    border-bottom: 12px solid transparent;
    margin-left: 4px;
  }

  @keyframes fadeInOut {
    0% {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.8);
    }
    30% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
    70% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }
    100% {
      opacity: 0;
      transform: translate(-50%, -50%) scale(0.8);
    }
  }
</style>
